import React, { useState, useEffect, useMemo } from "react";
import { ModelMetrics, EdaData } from "../../types";
import "./PredictionForm.css"; // Import CSS for styling
// Correct the import path
import { getFeatureStats, getFeatureType } from "./PredictionFormUtils.ts";
// Import common components if needed, e.g., InfoTooltip
// import { InfoTooltip } from '../common/InfoTooltip.tsx';
// Import the new Radar Chart component
import { InputRadarChart } from "./InputRadarChart.tsx";

// Update Props interface to include new state and callback
interface PredictionFormProps {
  modelData: ModelMetrics | null;
  edaData: EdaData | null;
  currentModelPath: string | null;
  // Props passed down from App.tsx
  isPredicting: boolean;
  predictionError: string | null;
  predictionResult: Record<string, number> | null;
  onPredict: (
    featuresPayload: Record<string, string | number>,
  ) => Promise<void>; // Callback function
}

export const PredictionForm: React.FC<PredictionFormProps> = ({
  modelData,
  edaData,
  currentModelPath,
  // Destructure new props
  isPredicting,
  predictionError,
  predictionResult,
  onPredict,
}) => {
  // Input values state remains local to the form
  const [inputValues, setInputValues] = useState<
    Record<string, string | number>
  >({});
  // REMOVED local state for isLoading, error, predictionResult
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [error, setError] = useState<string | null>(null);
  // const [predictionResult, setPredictionResult] = useState<Record<string, number> | null>(null);

  const requiredFeatures = useMemo(
    () => modelData?.model_metrics?.model_info?.required_features || [],
    [modelData],
  );

  // Effect to initialize form (predictionResult reset is handled in App.tsx)
  useEffect(() => {
    if (
      modelData &&
      modelData.model_metrics &&
      modelData.model_metrics.model_info &&
      requiredFeatures.length > 0
    ) {
      const initialValues: Record<string, string | number> = {};
      requiredFeatures.forEach((feature) => {
        const featureType = getFeatureType(modelData, feature);
        const stats = getFeatureStats(edaData, feature);
        if (featureType === "numeric") {
          initialValues[feature] = Math.round(stats?.mean ?? 0);
        } else if (featureType === "categorical") {
          initialValues[feature] = stats?.all_unique_values?.[0] ?? "";
        } else {
          initialValues[feature] = "";
        }
      });
      setInputValues(initialValues);
      // No need to reset predictionResult/error here, App does it
    } else {
      setInputValues({});
    }
    // Only depend on data needed for initialization
  }, [modelData, edaData, requiredFeatures]);

  // Input change handler remains the same
  const handleInputChange = (featureName: string, value: string | number) => {
    const featureType = getFeatureType(modelData, featureName);
    let processedValue: string | number = value;

    if (featureType === "numeric") {
      const num = parseFloat(value.toString());
      // Store the number if valid, otherwise fallback to 0 (or previous state value if needed)
      processedValue = isNaN(num) ? 0 : num;
    }
    // For categorical/unknown, value remains string or original type

    setInputValues((prev) => ({ ...prev, [featureName]: processedValue }));
  };

  // Simplified Handler - Calls the onPredict prop after validation
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // Keep local validation
    let localValidationError = false;
    let localValidationErrorMsg: string | null = null;
    const featuresPayload: Record<string, string | number> = {};

    for (const featureName of requiredFeatures) {
      const featureType = getFeatureType(modelData, featureName);
      const value = inputValues[featureName];
      if (value === undefined || value === null || value === "") {
        // Check for empty string too
        localValidationErrorMsg = `Fehlender Wert für Feature: ${featureName}`;
        localValidationError = true;
        break;
      }
      if (featureType === "numeric") {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          localValidationErrorMsg = `Ungültiger numerischer Wert für ${featureName}: ${value}`;
          localValidationError = true;
          break;
        }
        featuresPayload[featureName] = numValue;
      } else {
        featuresPayload[featureName] = String(value);
      }
    }

    if (localValidationError) {
      // Optionally show local validation error briefly?
      // For now, just prevent submission
      console.warn("Local Validation Error:", localValidationErrorMsg);
      return;
    }

    // Call the callback passed from App.tsx
    onPredict(featuresPayload);
  };

  // --- Render Logic ---
  if (
    !modelData ||
    !modelData.model_metrics ||
    !modelData.model_metrics.model_info
  ) {
    return (
      <p>
        Modellinformationen nicht verfügbar (benötigt model_metrics.model_info).
      </p>
    );
  }

  return (
    <div className="prediction-form-container card">
      <h3>Neue Vorhersage treffen</h3>
      <p>Modell: {modelData.model_metrics.model_info.name}</p>

      {requiredFeatures.length === 0 ? (
        <p>Keine benötigten Features im Modell definiert.</p>
      ) : (
        <>
          {" "}
          {/* Use Fragment to wrap form and chart */}
          <form onSubmit={handleSubmit} className="prediction-form" noValidate>
            {/* --- Dynamic Input Fields --- */}
            <div className="form-grid">
              {requiredFeatures.map((featureName) => {
                const featureType = getFeatureType(modelData, featureName);
                const stats = getFeatureStats(edaData, featureName);
                // Ensure currentValue is treated as number for numeric fields in rendering
                const currentValue = inputValues[featureName];
                const numericValue =
                  featureType === "numeric"
                    ? typeof currentValue === "number"
                      ? currentValue
                      : parseFloat(currentValue?.toString() || "0")
                    : 0; // Default for non-numeric cases

                const hasValidRange =
                  featureType === "numeric" &&
                  stats?.min !== undefined &&
                  stats?.max !== undefined &&
                  stats.min !== stats.max &&
                  !isNaN(stats.min) &&
                  !isNaN(stats.max);

                return (
                  <div key={featureName} className="form-group">
                    <label htmlFor={featureName}>{featureName}</label>
                    {featureType === "numeric" && (
                      <div className="numeric-input-group">
                        <input
                          type="number"
                          id={featureName}
                          name={featureName}
                          // Display the state value, which should be a number after input change
                          value={currentValue ?? ""}
                          onChange={(e) =>
                            handleInputChange(featureName, e.target.value)
                          }
                          step="any"
                          className="form-control"
                          required
                        />
                        {/* Display only Min/Max */}
                        <div className="min-max-current-display">
                          {stats?.min !== undefined &&
                          stats?.max !== undefined ? (
                            <span className="min-max-info">
                              Min: {stats.min.toFixed(2)} | Max:{" "}
                              {stats.max.toFixed(2)}
                            </span>
                          ) : (
                            <span />
                          )}
                        </div>

                        {/* Simple Range Slider Re-added */}
                        {hasValidRange && stats && (
                          <input
                            type="range"
                            min={stats.min}
                            max={stats.max}
                            step={
                              Number.isInteger(stats.min!) &&
                              Number.isInteger(stats.max!)
                                ? 1
                                : (stats.max! - stats.min!) / 100 || 0.01
                            }
                            value={numericValue}
                            onChange={(e) =>
                              handleInputChange(featureName, e.target.value)
                            } // Pass string value
                            className="form-range-simple"
                            title={`Range: ${stats.min} to ${stats.max}`}
                          />
                        )}
                      </div>
                    )}
                    {featureType === "categorical" &&
                      stats?.all_unique_values &&
                      stats.all_unique_values.length > 0 && (
                        <select
                          id={featureName}
                          name={featureName}
                          value={currentValue}
                          onChange={(e) =>
                            handleInputChange(featureName, e.target.value)
                          }
                          className="form-control"
                          required
                        >
                          {stats.all_unique_values.map(
                            (val: string | number) => (
                              <option key={val} value={val}>
                                {val}
                              </option>
                            ),
                          )}
                        </select>
                      )}
                    {featureType === "categorical" &&
                      (!stats?.all_unique_values ||
                        stats.all_unique_values.length === 0) && (
                        // Fallback text input for categorical if no unique values found in EDA
                        <input
                          type="text"
                          id={featureName}
                          name={featureName}
                          value={currentValue}
                          onChange={(e) =>
                            handleInputChange(featureName, e.target.value)
                          }
                          className="form-control"
                          required
                        />
                      )}
                    {featureType === "unknown" && (
                      <p className="text-warning">
                        Unbekannter Feature-Typ: {featureName}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              {/* Use isPredicting prop for disabled state */}
              <button type="submit" disabled={isPredicting}>
                {isPredicting ? "Vorhersage läuft..." : "Vorhersage treffen"}
              </button>
            </div>
          </form>
          {/* Conditionally render the Radar Chart below the form */}
          <InputRadarChart
            inputValues={inputValues}
            modelData={modelData}
            edaData={edaData}
          />
        </>
      )}

      {/* --- Prediction Result/Error Display (use props) --- */}
      {predictionResult && (
        <div className="prediction-result">
          <h4>Letzte Vorhersage:</h4> {/* Changed title slightly */}
          <ul>
            {Object.entries(predictionResult).map(([targetName, value]) => (
              <li key={targetName}>
                <strong>{targetName}:</strong> {value.toFixed(4)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Use predictionError prop */}
      {predictionError && (
        <div className="error-message prediction-error">
          Fehler bei der Vorhersage: {predictionError}
        </div>
      )}
    </div>
  );
};
